import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';

/**
 * Data access for the Takata dealer dashboard.
 *
 * One request per wave, holding every dealer in it. The screen's map, KPI tiles
 * and table are three views of that one set, filtered in the browser — which is
 * why there is no paged endpoint here and no `useServerDataGrid`.
 */

const BASE = '/api/stla/takata/dealers';

/** One dealer, one brand. A physical dealer appears once per brand it carries. */
export interface TakataDealer {
  name: string;
  country: string;
  city: string;
  address: string;
  phone: string;
  assignedBrand: string;
  latitude: number;
  longitude: number;
  averageWaitDays: number | null;
  categoryIndex: number | null;
  performanceColor: string;
  oic: string;
  physOut: string;
  zipCode: string;
  nuts3Code: string;
  hasAppointment: boolean;
  dealerService: string;
  truckTow: boolean | null;
  courtesyCar: boolean | null;
  totalCalls: number;
}

export interface PerformanceCategory {
  categoryIndex: number;
  label: string;
  maxDays: number;
  backgroundColor: string;
  gradientColors: string[];
}

export interface ThresholdInfo {
  categoryIndex: number;
  maxDays: number;
  categoryName: string;
  label: string;
}

export interface TakataDealersKpis {
  averageWaitDays: number;
  totalDealers: number;
  totalCalls: number;
  totalAnswered: number;
  minWaitTime: number;
  maxWaitTime: number;
  answerRate: number;
  totalAppointments: number;
}

export interface TakataDealersDashboard {
  availableWaves: string[];
  selectedWave: string | null;
  kpis: TakataDealersKpis;
  dealers: TakataDealer[];
  countries: string[];
  cities: string[];
  /** Keyed by category index as a string, because JSON object keys are strings. */
  performanceCategories: Record<string, PerformanceCategory>;
  /** Keyed by ISO country code, which is what the threshold rows are stored under. */
  thresholdsByCountry: Record<string, ThresholdInfo[]>;
  /** Country name to ISO code; the filter picks a name, the thresholds want a code. */
  countryCodes: Record<string, string>;
}

export const takataDealerKeys = {
  all: ['stla', 'takata-dealers'] as const,
  dashboard: (wave: string | null) => ['stla', 'takata-dealers', 'dashboard', wave] as const,
};

/** The whole dashboard for one wave; null wave means the most recent one. */
export function useTakataDealersDashboard(wave: string | null) {
  return useQuery<TakataDealersDashboard>({
    queryKey: takataDealerKeys.dashboard(wave),
    queryFn: () => api.get<TakataDealersDashboard>(BASE, { query: { wave } }),
    // A wave is closed data: re-fetching it on every remount buys nothing and
    // this payload is the largest the app requests.
    staleTime: 5 * 60 * 1000,
  });
}

/** The grey "no appointment" pseudo-category the server always includes. */
export const NO_APPOINTMENT_CATEGORY = 0;

/** Fallback colour for a category the server did not describe. */
export const UNKNOWN_CATEGORY_COLOR = '#6b7280';

export function categoryOf(
  categories: Record<string, PerformanceCategory> | undefined,
  categoryIndex: number | null | undefined,
): PerformanceCategory | undefined {
  return categories?.[String(categoryIndex ?? NO_APPOINTMENT_CATEGORY)];
}
